import { Dialog } from '@headlessui/react';
import { Copy, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'react-hot-toast';
import { formatPrice } from '@/lib/utils';
import { CartItem } from '@/types';
import { calculateCartItemDiscount } from '@/lib/discount';

interface PaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  paymentMethod: 'bank' | 'paystack' | 'monnify' | 'stripe';
  onPaymentMethodChange: (method: 'bank' | 'paystack' | 'monnify' | 'stripe') => void;
  onConfirm: () => void;
  storeInfo: any;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>, type: 'order' | 'shipping') => void;
  orderTotal: number;
  shippingTotal: number;
  vatAmount: number;
  orderPaymentReceipt: File | null;
  shippingPaymentReceipt: File | null;
  cart?: CartItem[];
  customerEmail?: string;
  customerName?: string;
}

export function PaymentMethodDialog({
  open,
  onClose,
  paymentMethod,
  onPaymentMethodChange,
  onConfirm,
  storeInfo,
  onFileUpload,
  orderTotal,
  shippingTotal,
  vatAmount,
  orderPaymentReceipt,
  shippingPaymentReceipt,
  cart = [],
  customerEmail,
  customerName,
}: PaymentMethodDialogProps) {
  const handleCopyAccountNumber = (accountNumber: string) => {
    navigator.clipboard.writeText(accountNumber);
    toast.success('Account number copied to clipboard');
  };

  const showSeparateShipping = storeInfo?.mb_active === 1 && storeInfo?.sepShipping === 1 && shippingTotal > 0;
  const total = orderTotal + shippingTotal + vatAmount;

  // Calculate discount information
  const cartTotals = cart.reduce((totals, item) => {
    const { totalOriginalPrice, totalDiscountValue, totalFinalPrice } = calculateCartItemDiscount(item);
    return {
      originalSubtotal: totals.originalSubtotal + totalOriginalPrice,
      totalDiscount: totals.totalDiscount + totalDiscountValue,
      finalSubtotal: totals.finalSubtotal + totalFinalPrice
    };
  }, { originalSubtotal: 0, totalDiscount: 0, finalSubtotal: 0 });

  const hasDiscounts = cartTotals.totalDiscount > 0;

  const handleStripePayment = async () => {
    const loadingToast = toast.loading('Processing payment...');
    try {
      if (!storeInfo?.StripeKey) {
        throw new Error('Stripe is not properly configured for this store. Please contact the store administrator.');
      }

      // Create checkout session through Supabase Edge Function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          amount: total,
          currency: 'usd',
          success_url: `${window.location.origin}/order-complete`,
          cancel_url: `${window.location.origin}/cart`,
        }),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }

      const { sessionId } = await response.json();

      // Initialize Stripe
      if (typeof (window as any).Stripe === 'undefined') {
        throw new Error('Stripe.js not loaded. Please refresh the page and try again.');
      }

      const stripe = (window as any).Stripe(storeInfo.StripeKey);
      if (!stripe) {
        throw new Error('Failed to initialize Stripe payment.');
      }

      // Redirect to Checkout
      const { error: redirectError } = await stripe.redirectToCheckout({
        sessionId
      });

      if (redirectError) {
        throw redirectError;
      }

      toast.dismiss(loadingToast);
    } catch (error) {
      toast.dismiss(loadingToast);
      console.error('Stripe payment error:', error);
      
      let errorMessage = 'Payment processing failed. ';
      if (error instanceof Error) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please try again or contact support if the issue persists.';
      }
      
      toast.error(errorMessage);
    }
  };

  const handlePayment = () => {
    if (paymentMethod === 'stripe') {
      handleStripePayment();
    } else {
      onConfirm();
    }
  };

  const getButtonLabel = () => {
    if (paymentMethod === 'bank') {
      return 'Confirm Payment';
    }
    return `Pay ${formatPrice(total)}`;
  };

  const renderAmountDetails = () => (
    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
      <h3 className="font-medium text-gray-900 dark:text-white mb-3">Order Summary</h3>
      <div className="space-y-2">
        {hasDiscounts && (
          <>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-300">Original Subtotal:</span>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 line-through">
                {formatPrice(cartTotals.originalSubtotal)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">Bulk Discounts:</span>
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                -{formatPrice(cartTotals.totalDiscount)}
              </span>
            </div>
          </>
        )}
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            {hasDiscounts ? 'Discounted Subtotal:' : 'Subtotal:'}
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(orderTotal)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-300">Shipping:</span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(shippingTotal)}</span>
        </div>
        {vatAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-300">TAX:</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{formatPrice(vatAmount)}</span>
          </div>
        )}
        <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
          <div className="flex justify-between">
            <span className="text-base font-bold text-gray-900 dark:text-white">Total:</span>
            <span className="text-base font-bold text-gray-900 dark:text-white">{formatPrice(total)}</span>
          </div>
          {hasDiscounts && (
            <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
              <div className="text-center">
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  🎉 You saved {formatPrice(cartTotals.totalDiscount)} with bulk discounts!
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
            <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
              Select Payment Method
            </Dialog.Title>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="max-h-[calc(100vh-16rem)] overflow-y-auto custom-scrollbar p-6">
            {renderAmountDetails()}
            
            <div className="space-y-4">
              {storeInfo?.mb_active === 1 && (
                <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="bank"
                      checked={paymentMethod === 'bank'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'bank')}
                      className="mr-3 text-brand-primary focus:ring-brand-primary"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900 dark:text-white">Bank Transfer</span>
                      {paymentMethod === 'bank' && (
                        <div className="mt-3 space-y-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Bank: <span className="font-bold">{storeInfo.m_bank}</span>
                          </p>

                          <div className="flex items-center space-x-2">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Account: <span className="font-bold">{storeInfo.m_ac_num}</span>
                            </p>
                            
                            <button
                              onClick={() => handleCopyAccountNumber(storeInfo.m_ac_name)}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded transition-colors"
                            >
                              <Copy className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                            </button>
                          </div>

                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Account Name: <span className="font-bold">{storeInfo.m_ac_name}</span>
                          </p>
                            
                          {showSeparateShipping && (
                            <>
                              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-2">
                                  Shipping Amount: <b>{formatPrice(shippingTotal)}</b>
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Shipping Payment Details:
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {storeInfo.sepShippingAcn}
                                </p>
                                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                                  Note: If shipping payment is combined with order payment, it will not be refunded and the order will not be shipped.
                                </p>
                              </div>
                            </>
                          )}
                          
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Upload Payment Receipt{showSeparateShipping ? ' (Order)' : ''}
                            </label>
                            <div className="flex items-center justify-center w-full">
                              <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                orderPaymentReceipt 
                                  ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20' 
                                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                              }`}>
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                  <Upload className={`w-8 h-8 mb-4 ${
                                    orderPaymentReceipt ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                                  }`} />
                                  {orderPaymentReceipt ? (
                                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                                      {orderPaymentReceipt.name}
                                    </p>
                                  ) : (
                                    <>
                                      <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        <span className="font-semibold">Click to upload</span> or drag and drop
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        PNG, JPG or PDF (MAX. 2MB)
                                      </p>
                                    </>
                                  )}
                                </div>
                                <input 
                                  type="file" 
                                  className="hidden" 
                                  accept="image/*,.pdf"
                                  onChange={(e) => onFileUpload(e, 'order')}
                                />
                              </label>
                            </div>

                            {showSeparateShipping && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Upload Shipping Payment Receipt
                                </label>
                                <div className="flex items-center justify-center w-full">
                                  <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                                    shippingPaymentReceipt 
                                      ? 'border-green-500 dark:border-green-400 bg-green-50 dark:bg-green-900/20' 
                                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                                  }`}>
                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                      <Upload className={`w-8 h-8 mb-4 ${
                                        shippingPaymentReceipt ? 'text-green-500 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'
                                      }`} />
                                      {shippingPaymentReceipt ? (
                                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                                          {shippingPaymentReceipt.name}
                                        </p>
                                      ) : (
                                        <>
                                          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                          </p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">
                                            PNG, JPG or PDF (MAX. 2MB)
                                          </p>
                                        </>
                                      )}
                                    </div>
                                    <input 
                                      type="file" 
                                      className="hidden" 
                                      accept="image/*,.pdf"
                                      onChange={(e) => onFileUpload(e, 'shipping')}
                                    />
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              )}

              {storeInfo?.pays_active === 1 && (
                <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="paystack"
                      checked={paymentMethod === 'paystack'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'paystack')}
                      className="mr-3 text-brand-primary focus:ring-brand-primary"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900 dark:text-white">Pay with Paystack</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Secure card payments powered by Paystack
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {storeInfo?.monify_active === 1 && (
                <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="monnify"
                      checked={paymentMethod === 'monnify'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'monnify')}
                      className="mr-3 text-brand-primary focus:ring-brand-primary"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900 dark:text-white">Pay with Monnify</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Secure payments via Monnify gateway
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {storeInfo?.stripeActive === 1 && (
                <div className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/30">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="stripe"
                      checked={paymentMethod === 'stripe'}
                      onChange={(e) => onPaymentMethodChange(e.target.value as 'stripe')}
                      className="mr-3 text-brand-primary focus:ring-brand-primary"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900 dark:text-white">Pay with Stripe</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Credit/debit cards, Apple Pay, Google Pay
                      </p>
                      {paymentMethod === 'stripe' && !storeInfo?.StripeKey && (
                        <p className="text-sm text-red-500 dark:text-red-400 mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                          Stripe payments are currently unavailable. Please choose another payment method or contact the store administrator.
                        </p>
                      )}
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePayment}
                disabled={paymentMethod === 'stripe' && !storeInfo?.StripeKey}
                className="min-w-[140px]"
              >
                {getButtonLabel()}
              </Button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}